import { sanitiseSignupSource } from "@/lib/signup-next";

/**
 * Employer self-reported attribution ("How did you hear about PeakWave?").
 * Optional at signup and stored per company in the service-only
 * `company_signup_attribution` table: `heard_about` (option id),
 * `heard_about_other` (free text, only for "other") and `signup_source` (the
 * first-party CTA that sent them, if any).
 *
 * Shared by the signup form (client) and company creation (server), so it
 * must stay free of server-only imports.
 */
export const HEARD_ABOUT_OPTIONS = [
  { value: "search", label: "Google or another search engine" },
  { value: "ai_assistant", label: "ChatGPT or another AI assistant" },
  { value: "peakwave_email", label: "An email from PeakWave" },
  { value: "facebook_group", label: "A Facebook group" },
  { value: "social_media", label: "Instagram, TikTok or other social media" },
  { value: "word_of_mouth", label: "Word of mouth or a colleague" },
  { value: "trade_body_partner", label: "A trade body or partner organisation" },
  { value: "news_article", label: "A news story or article" },
  { value: "seasonaire", label: "A seasonaire or candidate" },
  { value: "other", label: "Something else" },
] as const;

export type HeardAboutValue = (typeof HEARD_ABOUT_OPTIONS)[number]["value"];

export const HEARD_ABOUT_OTHER_MAX_LENGTH = 120;

const HEARD_ABOUT_VALUES = new Set<string>(HEARD_ABOUT_OPTIONS.map((option) => option.value));

export function isHeardAboutValue(value: unknown): value is HeardAboutValue {
  return typeof value === "string" && HEARD_ABOUT_VALUES.has(value);
}

function cleanOtherText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const cleaned = value
    .replace(/[\u0000-\u001F\u007F]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, HEARD_ABOUT_OTHER_MAX_LENGTH);
  return cleaned || null;
}

export type CompanySignupAttribution = {
  heard_about: HeardAboutValue | null;
  heard_about_other: string | null;
  signup_source: string | null;
};

/**
 * Validates the staged signup payload (auth metadata or pending row) into the
 * company columns. Unknown option ids are dropped; free text is kept only
 * with "other". Returns null when there is nothing to store.
 */
export function companySignupAttribution(
  signupData: Record<string, unknown>,
): CompanySignupAttribution | null {
  const heardAbout = isHeardAboutValue(signupData.heardAbout) ? signupData.heardAbout : null;
  const heardAboutOther = heardAbout === "other" ? cleanOtherText(signupData.heardAboutOther) : null;
  const signupSource = sanitiseSignupSource(signupData.entrySource);
  if (!heardAbout && !signupSource) return null;
  return {
    heard_about: heardAbout,
    heard_about_other: heardAboutOther,
    signup_source: signupSource,
  };
}

export function heardAboutLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return HEARD_ABOUT_OPTIONS.find((option) => option.value === value)?.label ?? value;
}
